'use client';

/**
 * Toast — transient feedback for user actions.
 *
 * Shows a brief message (e.g. "Beef Jerky geloggt") at the bottom of the
 * screen, auto-dismissing after a configurable duration. Combines visual
 * feedback with optional haptic feedback (vibration) on supporting devices.
 *
 * Usage:
 *   1. Wrap the app root with <ToastProvider>
 *   2. In any component: const toast = useToast();
 *   3. Call toast.show('Beef Jerky geloggt') or toast.error('Fehler')
 *
 * Design principles:
 *   - One toast at a time (newer replaces older — avoid stacking)
 *   - Bottom-of-screen, above the bottom nav (uses fixed positioning)
 *   - Haptic feedback fires on .show() — short pulse on success, none on error
 *     (errors should not feel rewarding)
 *   - Auto-dismiss after 2s default; configurable per-call
 *   - Manual dismiss via tap (one-tap to clear)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLegacyC } from '../theme';

// ─── Types ───

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastOptions {
  /** Auto-dismiss duration in ms. Default: 2000. */
  durationMs?: number;
  /** Override haptic behavior. Default: success=vibrate, error/info=silent. */
  haptic?: boolean;
}

interface ToastState {
  id: number;
  message: string;
  variant: ToastVariant;
  durationMs: number;
}

interface ToastApi {
  /** Generic show; defaults to 'success' variant. */
  show: (message: string, options?: ToastOptions) => void;
  /** Convenience: success-styled toast. */
  success: (message: string, options?: ToastOptions) => void;
  /** Convenience: error-styled toast (red, no haptic by default). */
  error: (message: string, options?: ToastOptions) => void;
  /** Convenience: info-styled toast (neutral). */
  info: (message: string, options?: ToastOptions) => void;
  /** Dismiss the current toast immediately. */
  dismiss: () => void;
}

// ─── Context ───

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Hook to access the toast API. Throws if used outside <ToastProvider>.
 * Throwing (rather than returning null) catches setup mistakes early —
 * a silent no-op would mask the missing provider.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast must be used within <ToastProvider>. Wrap your app root.'
    );
  }
  return ctx;
}

// ─── Haptic helper ───

/**
 * Fire a brief vibration if the device supports it. No-op on iOS Safari (which
 * doesn't expose navigator.vibrate) and on desktop. We don't try to detect
 * unsupported platforms — calling vibrate() with an unsupported pattern is
 * safe and silently fails.
 */
function tryHaptic(durationMs: number = 30): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(durationMs);
  } catch {
    // Some browsers throw on cross-origin or restricted contexts — ignore.
  }
}

// ─── Provider ───

export interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastState | null>(null);
  // Counter for unique IDs — used as React key to force re-mount of the same
  // visual when the same message fires twice in a row (e.g. user taps Hydration
  // +250ml twice quickly — both should feel acknowledged, not deduped).
  const idCounter = useRef(0);
  // Timer ref so we can clear/reset between rapid toasts.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast(null);
  }, []);

  const showInternal = useCallback(
    (message: string, variant: ToastVariant, options?: ToastOptions) => {
      // Clear any pending dismiss timer — new toast resets the clock.
      if (timerRef.current) clearTimeout(timerRef.current);

      idCounter.current += 1;
      const durationMs = options?.durationMs ?? 2000;
      const shouldHaptic =
        options?.haptic ?? (variant === 'success');

      if (shouldHaptic) tryHaptic();

      setToast({
        id: idCounter.current,
        message,
        variant,
        durationMs,
      });

      timerRef.current = setTimeout(() => {
        setToast(null);
        timerRef.current = null;
      }, durationMs);
    },
    []
  );

  // Cleanup pending timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Stable API — useCallback ensures consumers don't re-render on every tick.
  const api: ToastApi = {
    show: useCallback(
      (m, o) => showInternal(m, 'success', o),
      [showInternal]
    ),
    success: useCallback(
      (m, o) => showInternal(m, 'success', o),
      [showInternal]
    ),
    error: useCallback(
      (m, o) => showInternal(m, 'error', o),
      [showInternal]
    ),
    info: useCallback(
      (m, o) => showInternal(m, 'info', o),
      [showInternal]
    ),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toast={toast} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// ─── Viewport (the visual element) ───

interface ToastViewportProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

function ToastViewport({ toast, onDismiss }: ToastViewportProps) {
  const C = useLegacyC();

  if (!toast) return null;

  // Variant → colors. We use the legacy palette tokens since the rest of the
  // app reads from there. Toast doesn't need to support all 3 theme modes
  // independently — it follows the active mode like everything else.
  const palette = (() => {
    switch (toast.variant) {
      case 'error':
        return { bg: '#7F1D1D', fg: '#FFFFFF', accent: '#FCA5A5' };
      case 'info':
        return { bg: C.text, fg: C.bg, accent: C.a };
      case 'success':
      default:
        return { bg: C.text, fg: C.bg, accent: C.a };
    }
  })();

  return (
    <div
      // key forces re-mount of the inner element when ID changes — that re-
      // triggers the slide-in animation even when the message is identical.
      key={toast.id}
      onClick={onDismiss}
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: palette.bg,
        color: palette.fg,
        padding: '12px 18px',
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 600,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
        zIndex: 9999,
        cursor: 'pointer',
        maxWidth: 'calc(100% - 32px)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        animation: 'coachai-toast-in 0.18s ease-out',
        // Centered above the bottom nav (which is ~80px tall + safe-area).
      }}
    >
      <span style={{ marginRight: 8 }}>
        {toast.variant === 'error' ? '✕' : '✓'}
      </span>
      {toast.message}
      {/* Inline keyframes — no global CSS file changes needed. */}
      <style>{`
        @keyframes coachai-toast-in {
          from { opacity: 0; transform: translate(-50%, 12px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
